import './App.css';
import { createTheme, ThemeProvider, Box, CircularProgress } from '@mui/material'
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { lazy, Suspense, useEffect } from 'react';
import NotFoundpage from './components/404.page';
import PublicRoutes from './components/layouts/PublicRoutes';
import PrivateRoutes from './components/layouts/PrivateRoutes';
import Home from './v2/pages/Home';
import ProductsPage from './v2/pages/Products';
import FeaturedTeamsPage from './v2/pages/FeaturedTeams';
import ProductDetailPage from './v2/pages/ProductDetail';
import SearchResultsPage from './v2/pages/SearchResults';
import EVoucherPage from './v2/pages/EVoucher';
import FAQsPage from './v2/pages/FAQs';
import ContactPage from './v2/pages/Contact';
import AccountPage from './v2/pages/Account';
import WishlistPage from './v2/pages/Wishlist';
import CartPage from './v2/pages/Cart';
import Login from './components/customer/auth/login';
import Blogs from './components/customer/blogs';
import Register from './components/customer/auth/register';
import ForgotPassword from './components/customer/auth/forgotPassword';
import PasswordReset from './components/customer/auth/passwordReset';
import PasswordChange from './components/customer/auth/passwordChange';
import CheckoutPage from './v2/pages/Checkout';
import CustomerPrivateRoutes from './components/layouts/CustomerPrivateRoutes';
import BusinessRoutes from './components/layouts/BusinessRoutes';
import DynamicStyles, { BASE_FONT_SIZES, HEADING_FONT_SIZES, BUTTON_FONT_SIZES } from './components/common/DynamicStyles';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useSelector } from 'react-redux';
import TermsAndConditions from './components/customer/links/termsAndConditions';
import ReturnsAndExchanges from './components/customer/links/returnsAndExchanges';
import ShippingAndDelivery from './components/customer/links/shippingAndDelivery';
import PrivacyPolicy from './components/customer/links/privacyPolicy';
import GroupedProductsPage from './components/customer/productsComponent/groupedproducts';

// Admin dashboard (lazy-loaded, code-split out of the storefront bundle)
const Dashboard = lazy(() => import('./components/Dashboard'));
const ConfigurationZed = lazy(() => import('./components/configuration/configuration'));
const EvouchersCampaigns = lazy(() => import('./components/evouchers/campaigns'));
const PurchasedVocuhers = lazy(() => import('./components/evouchers/purchased'));
const PurchasedDetails = lazy(() => import('./components/evouchers/purchasedDetails'));
const AdditionalServices = lazy(() => import('./components/products/additionalServices'));
const FeaturedTeams = lazy(() => import('./admin/components/FeaturedTeams'));
const PackagingFee = lazy(() => import('./components/products/packagingFee'));
const EditAdditionalServices = lazy(() => import('./components/products/editAdditionalServices'));
const Orders = lazy(() => import('./components/Orders/Orders'));
const OrderDetails = lazy(() => import('./components/Orders/OrderDetails'));
const Products = lazy(() => import('./components/products/Products'));
const CustomerAdminComponent = lazy(() => import('./components/customers/customerAdmin'));
const CustomerOrders = lazy(() => import('./components/customers/customerOrders'));
const CustomerAllOrders = lazy(() => import('./components/customers/customerAllOrders'));
const Reports = lazy(() => import('./components/reports/reportsActivity'));
const Categories = lazy(() => import('./components/Categories/Categories'));
const AddProducts = lazy(() => import('./components/products/AddProduct'));
const AddCategories = lazy(() => import('./components/Categories/AddCategory'));
const EditCategory = lazy(() => import('./components/Categories/EditCategory'));
const EditProduct = lazy(() => import('./components/products/EditProduct'));
const ShippingRegion = lazy(() => import('./components/deliveryMethods/shippingRegion'));
const PickupRegions = lazy(() => import('./components/deliveryMethods/pickupRegions'));
const TagsComponent = lazy(() => import('./components/tags/tagsComponent'));
const VariationsComponent = lazy(() => import('./components/variations/variationsComponent'));
const PreOrders = lazy(() => import('./components/preorders/preorders'));
const CustomizeStore = lazy(() => import('./components/configuration/customizeStore'));
const AdminLogin = lazy(() => import('./components/customer/auth/admin/authLogin'));

// Heavy but non-critical-path storefront/account routes
const QrcodeComponent = lazy(() => import('./components/reactBusinessQrcodeGenerate/reactQrcode').then(module => ({ default: module.QrcodeComponent })));
const OrderPaidSuccess = lazy(() => import('./components/Orders/orderPaidSuccess'));
const AdminBillingLock = lazy(() => import('./components/common/AdminBillingLock'));
const CustomerProfile = lazy(() => import('./components/customer/profile/customerProfile').then(module => ({ default: module.CustomerProfile })));
const CustomerOrderDetails = lazy(() => import('./components/customer/profile/customerOrderDetails'));

const SuspenseFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
    <CircularProgress />
  </Box>
);


const App = () => {
  const typography = useSelector((store) => store.template.typography);
  const businessName = useSelector((store) => store.configs.businessName);
  const colors = useSelector((store) => store.template.colors);
  const buttons = useSelector((store) => store.template.buttons);

  useEffect(() => {
    if (businessName) {
      window.__stopTitleAnimation?.();
      document.title = businessName;
    }
  }, [businessName]);

  const baseFontSize = BASE_FONT_SIZES[typography?.baseSize] || BASE_FONT_SIZES.medium;
  const h1FontSize = HEADING_FONT_SIZES[typography?.headingSize] || HEADING_FONT_SIZES.medium;
  const buttonFontSize = BUTTON_FONT_SIZES[typography?.buttonSize] || BUTTON_FONT_SIZES.medium;

  const theme = createTheme({
    palette: {
      primary: { main: colors?.primary || '#032541' },
      secondary: { main: colors?.secondary || '#ffffff' },
    },
    typography: {
      fontFamily: typography?.body || 'Poppins, sans-serif',
      fontSize: baseFontSize - 2, // MUI's scale default is 14 against a 16px base
      h1: { fontSize: h1FontSize },
      h2: { fontSize: Math.round(h1FontSize * 0.8125) },
      h3: { fontSize: Math.round(h1FontSize * 0.6875) },
      h4: { fontSize: Math.round(h1FontSize * 0.5625) },
      button: { fontSize: buttonFontSize },
    },
    components: {
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: "#dc3545",
            "&$error": {
              color: "#dc3545",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: Number.isFinite(Number(buttons?.borderRadius)) ? Number(buttons.borderRadius) : 5,
          },
        },
      },
    },
  });
  return (
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <DynamicStyles />
      <Router>
        <ErrorBoundary>
        <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route element={<PublicRoutes />}>
            <Route element={<BusinessRoutes isNotAuthRoute={true} />}>
              <Route path="/" element={<Home />} />
              <Route path='/featured-teams' element={<FeaturedTeamsPage />} />
              <Route path='/products' element={<ProductsPage />} />
              <Route path='/groupedproducts/:parentCategoryId' element={<GroupedProductsPage />} />
              <Route path='/search' element={<SearchResultsPage />} />
              <Route path='/evoucher' element={<EVoucherPage />} />
              <Route path='/products/:id' element={<ProductDetailPage />} />
              <Route path='/contact' element={<ContactPage />} />
              <Route path='/blogs' element={<Blogs />} />
              <Route path='/faqs' element={<FAQsPage />} />
              <Route path='/cart' element={<CartPage />} />
              <Route path='/wishlist' element={<WishlistPage />} />
              <Route path='/account' element={<AccountPage />} />
              <Route path="*" element={<NotFoundpage />} />
              <Route path="/qrcode" element={<QrcodeComponent />} />

              <Route path='/terms' element={<TermsAndConditions />} />
              <Route path='/returns' element={<ReturnsAndExchanges />} />
              <Route path='/shipping' element={<ShippingAndDelivery />} />
              <Route path='/privacy' element={<PrivacyPolicy />} />
            </Route>
            <Route element={<BusinessRoutes isNotAuthRoute={false} />}>
              <Route path='/login'  element={<Login />} />
              <Route path='/register' element={<Register />} />
              <Route path='/password/forgot' element={<ForgotPassword />} />
              <Route path='/password/reset' element={<PasswordReset />} />
              <Route path='/password/change' element={<PasswordChange />} />
            </Route>

            <Route path='/zed_ecommerce/admin/login' element={<AdminLogin />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            <Route path="/zed_ecommerce/dashboard" element={<Dashboard />} />
            <Route path="/zed_ecommerce/billing" element={<AdminBillingLock />} />
            <Route path="/zed_ecommerce/configurations" element={<ConfigurationZed />} />
            <Route path="/zed_ecommerce/evouchercompaings" element={<EvouchersCampaigns/>} />
            <Route path="/zed_ecommerce/evoucherpurchased" element={<PurchasedVocuhers/>} />
            <Route path="/zed_ecommerce/evoucher/:voucherId" element={<PurchasedDetails/>} />
            <Route path="/zed_ecommerce/additionalServices" element={<AdditionalServices />} />
            <Route path="/zed_ecommerce/featuredTeams" element={<FeaturedTeams />} />
            <Route path="/zed_ecommerce/packaging" element={<PackagingFee />} />
            <Route path="/zed_ecommerce/additionalServices/:serviceId/edit" element={<EditAdditionalServices />} />
            <Route path="/zed_ecommerce/orders" element={<Orders />} />
            <Route path="/zed_ecommerce/orders/:orderId/details" element={<OrderDetails />} />
            <Route path="/zed_ecommerce/products" element={<Products />} />
            <Route path="/zed_ecommerce/customers" element={<CustomerAdminComponent />} />
            <Route path="/zed_ecommerce/customer/:customerId/details" element={<CustomerOrders />} />
            <Route path="/zed_ecommerce/customer/:customerId/orders" element={<CustomerAllOrders />} />
            <Route path="/zed_ecommerce/reports" element={<Reports />} />
            <Route path="/zed_ecommerce/categories" element={<Categories />} />
            <Route path="/zed_ecommerce/addProducts" element={<AddProducts />} />
            <Route path="/zed_ecommerce/addCategories" element={<AddCategories />} />
            <Route path="/zed_ecommerce/categories/:categoryId/edit" element={<EditCategory />} />
            <Route path="/zed_ecommerce/products/:productId/edit" element={<EditProduct />} />
            <Route path="/zed_ecommerce/shippingRegions" element={<ShippingRegion />} />
            <Route path="/zed_ecommerce/pickups" element={<PickupRegions />} />
            <Route path="/zed_ecommerce/tags" element={<TagsComponent />} />
            <Route path="/zed_ecommerce/variations" element={<VariationsComponent />} />
            <Route path="/zed_ecommerce/preOrders" element={<PreOrders />} />
            <Route path="/zed_ecommerce/customizeStore" element={<CustomizeStore />} />
          </Route>

          <Route element={<CustomerPrivateRoutes />}>
            <Route path="/order/customer/:customerId/:orderId" element={<OrderPaidSuccess />} />
            <Route path='/zed-ecommerce/customer-profile' element={<CustomerProfile />} />
            <Route path='/checkout' element={<CheckoutPage />} />
            <Route path='/customer/order/:orderId' element={<CustomerOrderDetails />} />
          </Route>
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
